/**
 * 产品向导的表单模型（WizardDraft）⇄ 后端 API payload 的纯函数转换。
 * 客户在界面上只看到中文表单，schema_json / rules / modifiers 全部在这里生成。
 */
import type { Product, Rule, Modifier, CreateProductPayload } from "../api";

export type Measure = "area" | "length";
export type TierUnit = "perSheet" | "perSquare" | "perCm";

export interface TierRow {
  min: string;        // 数字字符串，空 = 0
  max: string;        // 空 = 不封顶
  inclusive: boolean; // max 是否含等于
  price: string;
  unit: TierUnit;
}

export interface SpecRow {
  label: string;      // 规格名，如 "5mm" / "5+3"
  min: string;        // 数值范围（可选，用于按数值自动匹配规格）
  max: string;
  tiers: TierRow[];
}

export interface ModifiersDraft {
  minPrice: string;      // 最低起价（元），空 = 无
  doubleSided: string;   // 双面倍数，空 = 无
  edgeFee: string;       // 包边费（元/米），空 = 无
  cuttingFee: string;    // 裁切费（元/张），空 = 无
  smallBatch: boolean;   // 小批量特价（首张按档位价，后续按低价）
  smallBatchYuan: string;
  /** 小批量特价：前 N 张按档位价，缺省 "1"（AI 解析出的草稿没有该字段） */
  smallBatchFirstN?: string;
  /**
   * 编辑已有产品时加载到的修饰项原文（modifier_type + params_json 原字符串 + is_active）。
   * 保存时：表单不认识的修饰项原样写回；认识的修饰项若数值未变，写回原字符串，
   * 数值变了则在原参数基础上合并，保留表单没有覆盖的字段。
   * 停用（is_active=0）的修饰项不填进表单，保存时原样写回（后端 PUT 会删除没传回的行）；
   * 若用户在表单里给它填了值，则视为重新启用。
   */
  loaded?: Modifier[];
}

export interface WizardDraft {
  key: string;
  name: string;
  keywords: string;      // 逗号/空格分隔
  sortOrder: number;
  measure: Measure;
  twoLevel: boolean;     // measure=length 时：是否带规格层（如厚度）
  specLabel: string;     // 规格层名称，默认 "厚度"
  tiers: TierRow[];      // measure=area 或单层 length
  specs: SpecRow[];      // 两层 length
  modifiers: ModifiersDraft;
}

export const AREA_UNITS: { value: TierUnit; label: string }[] = [
  { value: "perSheet", label: "元/张（固定价）" },
  { value: "perSquare", label: "元/平米" },
];
export const LENGTH_UNITS: { value: TierUnit; label: string }[] = [
  { value: "perCm", label: "元/厘米" },
  { value: "perSquare", label: "元/平米" },
];

export function emptyTier(unit: TierUnit): TierRow {
  return { min: "", max: "", inclusive: false, price: "", unit };
}

export function emptyModifiers(): ModifiersDraft {
  return {
    minPrice: "", doubleSided: "", edgeFee: "", cuttingFee: "",
    smallBatch: false, smallBatchYuan: "1", smallBatchFirstN: "1", loaded: [],
  };
}

export function emptyDraft(measure: Measure = "area"): WizardDraft {
  return {
    key: "", name: "", keywords: "", sortOrder: 0,
    measure, twoLevel: measure === "length", specLabel: "厚度",
    tiers: [emptyTier(measure === "area" ? "perSheet" : "perCm")],
    specs: [{ label: "", min: "", max: "", tiers: [emptyTier("perCm")] }],
    modifiers: emptyModifiers(),
  };
}

// ── 校验 ─────────────────────────────────────────────────────────

const num = (s: string): number | null => {
  if (s.trim() === "") return null;
  const v = Number(s);
  return Number.isFinite(v) ? v : NaN as unknown as number;
};

function validateTiers(tiers: TierRow[], what: string): string | null {
  if (!tiers.length) return `${what}至少需要一个价格档位`;
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i];
    const p = num(t.price);
    if (p === null || Number.isNaN(p) || p < 0) return `${what}第 ${i + 1} 档：请填写有效单价`;
    const mn = num(t.min), mx = num(t.max);
    if (mn !== null && Number.isNaN(mn)) return `${what}第 ${i + 1} 档：下限不是数字`;
    if (mx !== null && Number.isNaN(mx)) return `${what}第 ${i + 1} 档：上限不是数字`;
    if (mn !== null && mx !== null && mx <= mn) return `${what}第 ${i + 1} 档：上限必须大于下限`;
    if (i < tiers.length - 1 && mx === null) return `${what}第 ${i + 1} 档：非最后一档需要填写上限`;
  }
  return null;
}

export function validateDraft(d: WizardDraft, isEdit: boolean): string | null {
  if (!isEdit && !d.key.trim()) return "产品标识不能为空";
  if (!isEdit && !/^[a-z][a-z0-9_]*$/.test(d.key.trim()))
    return "产品标识只能用小写字母、数字和下划线，且以字母开头";
  if (!d.name.trim()) return "产品名称不能为空";
  if (d.measure === "length" && d.twoLevel) {
    if (!d.specs.length) return "至少需要一个规格";
    for (let i = 0; i < d.specs.length; i++) {
      const s = d.specs[i];
      if (!s.label.trim()) return `规格 ${i + 1}：请填写规格名称`;
      const e = validateTiers(s.tiers, `规格「${s.label}」`);
      if (e) return e;
    }
  } else {
    const e = validateTiers(d.tiers, "");
    if (e) return e;
  }
  const m = d.modifiers;
  for (const [v, label] of [[m.minPrice, "最低起价"], [m.doubleSided, "双面倍数"],
    [m.edgeFee, "包边费"], [m.cuttingFee, "裁切费"], [m.smallBatchYuan, "小批量后续单价"],
    [m.smallBatchFirstN ?? "", "小批量首批张数"]] as const) {
    const n = num(v);
    if (n !== null && (Number.isNaN(n) || n < 0)) return `${label}不是有效数字`;
  }
  if (m.smallBatch && (m.smallBatchFirstN ?? "").trim() !== "") {
    const n = Number(m.smallBatchFirstN);
    if (!Number.isInteger(n) || n < 1) return "小批量首批张数必须是不小于 1 的整数";
  }
  return null;
}

// ── 生成 label（中文描述，报价结果里会展示给终端客户）────────────

const UNIT_TEXT: Record<TierUnit, string> = {
  perSheet: "元/张", perSquare: "元/平米", perCm: "元/厘米",
};

export function tierLabel(t: TierRow, measure: Measure): string {
  const dim = measure === "area" ? "面积" : "尺寸";
  const u = measure === "area" ? "㎡" : "cm";
  const mn = t.min.trim() || "0";
  const cmp = t.inclusive ? "≤" : "<";
  const range = t.max.trim() === ""
    ? `${dim} > ${mn}${u}`
    : `${mn}${u} ≤ ${dim} ${cmp} ${t.max}${u}`;
  return `${range}: ${t.price}${UNIT_TEXT[t.unit]}`;
}

// ── Draft → API payloads ─────────────────────────────────────────

export function draftToProduct(d: WizardDraft, calcStrategy?: string): CreateProductPayload {
  const keywords = d.keywords.split(/[,，;；\s]+/).map((s) => s.trim()).filter(Boolean);
  const schema = {
    measure: d.measure,
    keywords,
    fields: d.measure === "area"
      ? [
          { key: "width", type: "float", unit: "m", required: true },
          { key: "height", type: "float", unit: "m", required: true },
          { key: "quantity", type: "int", default: 1 },
          { key: "edge_length", type: "float", unit: "m", required: false },
          { key: "cutting_required", type: "bool" },
          { key: "double_sided", type: "bool" },
        ]
      : [
          { key: "longest_side_cm", type: "float", unit: "cm", required: true },
          { key: "quantity", type: "int", default: 1 },
          { key: "thickness_mm", type: "float", unit: "mm" },
          { key: "thickness_spec", type: "string" },
        ],
  };
  return {
    key: d.key.trim(),
    name: d.name.trim(),
    calc_strategy: calcStrategy ?? "generic_tiered",
    schema_json: JSON.stringify(schema),
    sort_order: d.sortOrder,
  };
}

function tierCondition(t: TierRow): string {
  const cond: Record<string, unknown> = {};
  cond.min = t.min.trim() === "" ? 0 : Number(t.min);
  if (t.max.trim() !== "") {
    cond.max = Number(t.max);
    cond.inclusive = t.inclusive;
  }
  return JSON.stringify(cond);
}

export function draftToRules(d: WizardDraft): Rule[] {
  if (d.measure === "length" && d.twoLevel) {
    const rules: Rule[] = [];
    d.specs.forEach((s, i) => {
      const localId = `spec${i + 1}`;
      const cond: Record<string, unknown> = { eq: s.label.trim() };
      if (s.min.trim() !== "") { cond.min = Number(s.min); cond.inclusive = true; }
      if (s.max.trim() !== "") { cond.max = Number(s.max); cond.inclusive = true; }
      rules.push({
        _local_id: localId, dimension: "thickness_spec_label",
        condition_json: JSON.stringify(cond),
        label: s.label.trim(), price: undefined, unit: undefined, sort_order: i + 1,
      });
      s.tiers.forEach((t, j) => {
        rules.push({
          parent_id: localId as unknown as number, dimension: "longest_side_cm",
          condition_json: tierCondition(t),
          label: tierLabel(t, "length"),
          price: Number(t.price), unit: t.unit, sort_order: j + 1,
        });
      });
    });
    return rules;
  }
  const dim = d.measure === "area" ? "area_sqm" : "longest_side_cm";
  return d.tiers.map((t, i) => ({
    dimension: dim,
    condition_json: tierCondition(t),
    label: tierLabel(t, d.measure),
    price: Number(t.price), unit: t.unit, sort_order: i + 1,
  }));
}

/** 表单直接管理的修饰项类型（其余类型在向导里只读保留） */
export const KNOWN_MODIFIER_TYPES = [
  "min_price", "double_sided_multiplier", "edge_fee", "cutting_fee", "small_batch_pricing",
] as const;

const isKnownType = (t: string) => (KNOWN_MODIFIER_TYPES as readonly string[]).includes(t);

/** 表单 → 已知修饰项的参数（只含表单管理的字段）；null = 未启用 */
function formModifierParams(m: ModifiersDraft): Map<string, Record<string, unknown>> {
  const out = new Map<string, Record<string, unknown>>();
  const n = (s: string) => Number(s);
  if (m.minPrice.trim() !== "" && n(m.minPrice) > 0) out.set("min_price", { floor: n(m.minPrice) });
  if (m.doubleSided.trim() !== "" && n(m.doubleSided) > 0)
    out.set("double_sided_multiplier", { factor: n(m.doubleSided) });
  if (m.edgeFee.trim() !== "" && n(m.edgeFee) > 0) out.set("edge_fee", { per_meter: n(m.edgeFee) });
  if (m.cuttingFee.trim() !== "" && n(m.cuttingFee) > 0) out.set("cutting_fee", { per_sheet: n(m.cuttingFee) });
  if (m.smallBatch) {
    const firstN = (m.smallBatchFirstN ?? "").trim();
    out.set("small_batch_pricing", {
      first_n_at_tier: firstN === "" ? 1 : n(firstN),
      subsequent_yuan: m.smallBatchYuan.trim() === "" ? 1 : n(m.smallBatchYuan),
    });
  }
  return out;
}

/** 新建 small_batch_pricing 时的默认附加字段 */
const NEW_DEFAULTS: Record<string, Record<string, unknown>> = {
  small_batch_pricing: { applies_to_unit: "perSheet" },
};

const sameParams = (a: Record<string, unknown>, b: Record<string, unknown>) => {
  const ka = Object.keys(a), kb = Object.keys(b);
  return ka.length === kb.length && ka.every((k) => JSON.stringify(a[k]) === JSON.stringify(b[k]));
};

export function draftToModifiers(d: WizardDraft): Modifier[] {
  const m = d.modifiers;
  const form = formModifierParams(m);
  const loaded = m.loaded ?? [];
  const out: Modifier[] = [];
  const done = new Set<string>();

  // 1) 按加载时的顺序处理已有修饰项
  for (const orig of loaded) {
    const type = orig.modifier_type;
    if (done.has(type)) continue;
    done.add(type);
    const rawParams = typeof orig.params_json === "string" ? orig.params_json
      : JSON.stringify(orig.params_json ?? {});
    const inactive = orig.is_active === 0;
    if (!isKnownType(type)) {
      out.push({ modifier_type: type, params_json: rawParams, is_active: inactive ? 0 : 1 });
      continue;
    }
    const fp = form.get(type);
    if (!fp) {
      // 停用的行表单里本来就是空的: 原样保留; 启用的行被清空 = 删除
      if (inactive) out.push({ modifier_type: type, params_json: rawParams, is_active: 0 });
      continue;
    }
    const origParams = parseJson(rawParams);
    const merged = { ...origParams, ...fp };
    out.push({
      modifier_type: type,
      params_json: sameParams(merged, origParams) ? rawParams : JSON.stringify(merged),
      is_active: 1,
    });
  }
  // 2) 表单新启用的修饰项
  for (const type of KNOWN_MODIFIER_TYPES) {
    if (done.has(type)) continue;
    const fp = form.get(type);
    if (fp) out.push({ modifier_type: type, params_json: JSON.stringify({ ...fp, ...NEW_DEFAULTS[type] }), is_active: 1 });
  }
  return out;
}

/** 后端修饰项（GET /modifiers 的 data，含停用行）→ 表单 */
export function modifiersToDraft(modifiers: Modifier[]): ModifiersDraft {
  const mods = emptyModifiers();
  const loaded: Modifier[] = [];
  for (const m of modifiers) {
    const inactive = m.is_active === 0;
    loaded.push({
      modifier_type: m.modifier_type,
      params_json: typeof m.params_json === "string" ? m.params_json : JSON.stringify(m.params_json ?? {}),
      is_active: inactive ? 0 : 1,
    });
    if (inactive) continue; // 停用的不填表单, 保存时原样写回
    const params = parseJson(m.params_json);
    switch (m.modifier_type) {
      case "min_price": mods.minPrice = String(params.floor ?? ""); break;
      case "double_sided_multiplier": mods.doubleSided = String(params.factor ?? ""); break;
      case "edge_fee": mods.edgeFee = String(params.per_meter ?? ""); break;
      case "cutting_fee": mods.cuttingFee = String(params.per_sheet ?? ""); break;
      case "small_batch_pricing":
        mods.smallBatch = true;
        mods.smallBatchYuan = String(params.subsequent_yuan ?? "1");
        mods.smallBatchFirstN = String(params.first_n_at_tier ?? "1");
        break;
    }
  }
  mods.loaded = loaded;
  return mods;
}

/** 表单不直接管理的修饰项（未知类型或已停用；保存时原样保留），供界面提示 */
export function preservedModifiers(m: ModifiersDraft): Modifier[] {
  return (m.loaded ?? []).filter((x) => !isKnownType(x.modifier_type) || x.is_active === 0);
}

// ── API 数据 → Draft（编辑已有产品时反解）────────────────────────

const parseJson = (v: string | Record<string, unknown> | undefined | null): Record<string, unknown> => {
  if (!v) return {};
  if (typeof v !== "string") return v;
  try { return JSON.parse(v) as Record<string, unknown>; } catch { return {}; }
};

function condToTierFields(cond: Record<string, unknown>) {
  return {
    min: cond.min !== undefined && cond.min !== 0 ? String(cond.min) : (cond.min === 0 ? "0" : ""),
    max: cond.max !== undefined ? String(cond.max) : "",
    inclusive: Boolean(cond.inclusive),
  };
}

export function productToDraft(p: Product, rules: Rule[], modifiers: Modifier[]): WizardDraft {
  const schema = parseJson(p.schema_json);
  const measure: Measure =
    p.calc_strategy === "thickness_size_tiered" ? "length"
    : p.calc_strategy === "area_tiered" ? "area"
    : (schema.measure as Measure) === "length" ? "length" : "area";

  const keywords = Array.isArray(schema.keywords) ? (schema.keywords as string[]).join(", ") : "";
  const topRules = rules.filter((r) => !r.parent_id);
  const hasChildren = rules.some((r) => r.parent_id);

  const toTier = (r: Rule): TierRow => {
    const cond = parseJson(r.condition_json);
    return {
      ...condToTierFields(cond),
      price: r.price !== undefined && r.price !== null ? String(r.price) : "",
      unit: (r.unit as TierUnit) || (measure === "area" ? "perSheet" : "perCm"),
    };
  };

  const draft = emptyDraft(measure);
  draft.key = p.key;
  draft.name = p.name;
  draft.keywords = keywords;
  draft.sortOrder = p.sort_order;
  draft.twoLevel = measure === "length" && hasChildren;

  if (draft.twoLevel) {
    draft.specs = topRules.map((spec) => {
      const cond = parseJson(spec.condition_json);
      const kids = rules.filter((r) => r.parent_id === spec.id);
      return {
        label: spec.label || "",
        min: cond.min !== undefined ? String(cond.min) : "",
        max: cond.max !== undefined ? String(cond.max) : "",
        tiers: kids.length ? kids.map(toTier) : [emptyTier("perCm")],
      };
    });
  } else {
    draft.tiers = topRules.length ? topRules.map(toTier)
      : [emptyTier(measure === "area" ? "perSheet" : "perCm")];
  }

  const mods = modifiersToDraft(modifiers);
  draft.modifiers = mods;
  return draft;
}
